import { Moment } from 'moment';
import { ECanNotReapplyReason } from 'data/types/application.types';
import {
  TMultiOptionTypeMaterialUI,
  TOptionTypeUi,
  TPolicyUI,
  TStatusChangeInfoDB,
  TStatusChangeInfoUI,
  TStatusInfoWithSourceUI,
} from './general.types';
import {
  EAnimalPaymentStatusDB,
  EAnimalPaymentStatusUI,
  EAnimalTypes,
  EAnimalTypesUI,
  TAnimalStatusDetailsDB,
  TAnimalStatusDetailsUI,
  TAnimalWeightDB,
  TStatusInfoWithSourceDB,
} from './animals.types';
import { EVerificationStatus, EVerificationStatusUi } from './generalTypes';
import { TPropertyUI, TUnitUI } from './property.types';
import { TSpeciesProps } from './application.types';

export type TResidenceUnitDataDB = {
  id: string;
  number: string;
};

export type TResidencePropertyDataDb = {
  name: string;
  propertyId: string;
};

export type TResidenceUnitDataUI = {
  id: string;
  number: string;
};

export type TResidencePropertyDataUI = {
  id: string;
  name: string;
};

export type TResidenceDB = {
  residenceId: string;
  residentName: string;
  unit: TResidenceUnitDataDB;
  property: TResidencePropertyDataDb;
  status: EResidenceStatusDB;
};

export type TResidenceUI = {
  residenceId: string;
  residentName: string;
  unit: TResidenceUnitDataUI;
  property: TResidencePropertyDataUI;
  status: EResidenceStatusUi;
};

export type TResidenciesOptionsDB = {
  name: string;
  value: string;
  status: EResidenceStatusesDB;
};

export type TResidenciesOptionsUI = {
  label: string;
  value: string;
  status: EResidenceStatusesUI;
  moveInDate?: string;
  // PP-10057: the create-animal modal looks up the property's managed animal types once a residency
  // is chosen. Null for a residency at a custom property, which has no OPP configuration.
  propertyId: string | null;
};

export type TResidenceFilterDB = {
  propertyName: string | null;
  propertyId: string | null;
  residentName: string;
  unitNumber: string | null;
  residenceIds: string[];
  // residencyApplicationIds: string[];
  residencyIdToDetails: { [id: string]: { status: EResidenceStatusesDB } };
  moveInDate?: string;
};

export type TResidenceFilterUI = {
  residenceProfile: string;
  residenceIds: string[];
  // residencyApplicationIds: string[];
  residenciesOptions: TResidenciesOptionsUI;
  moveInDate?: string;
};

export type TResidenceMoveInfoUI = {
  isPolicyChanging: boolean;
};

export type TResidenceMoveInfo = {
  isPolicyChanging: boolean;
};

export type TResidentMoveFormDataProps = {
  property: TOptionTypeUi;
  unit: TOptionTypeUi;
  startTime: Moment;
  endTime: Moment;
};

export type TAnimalIndicationFormData = {
  hasAnimal: boolean;
  animalCount?: number;
  roommateHasAnimal?: boolean;
};

export type TResidentNoticeFormDataProps = {
  moveOutDate: Moment;
};

export type TResidentNoticeUi = TResidentNoticeFormDataProps & {
  residenceId: string;
  shouldAutomaticallyMoveOutAfterNotice: boolean;
};

export type TMoveOutToResidentUi = {
  residencyId: string;
  reason?: string;
  animalResidencyIds: string[];
};
export type TMoveOutToResidentDb = {
  residencyId: string;
  reason?: string;
  animalResidencyIds: string[];
};

export type TMoveOutToNoticeUi = TMoveOutToResidentUi & {
  moveOutDate: Moment;
  shouldAutomaticallyMoveOutAfterNotice: boolean;
};
export type TMoveOutToNoticeDb = TMoveOutToResidentDb & {
  moveOutDate: string;
  shouldAutomaticallyMoveOutAfterNotice: boolean;
};

export type TResidentNotice = {
  tenantResidencyId: string;
  moveOutDate: string;
  shouldAutomaticallyMoveOutAfterNotice: boolean;
};

export type TResidentMoveUi = TResidentMoveFormDataProps & {
  residenceId: string;
};

export type TResidentMove = {
  residenceId: string;
  propertyId: string;
  unitNumber: string;
  startTime?: string;
  endTime?: string;
};

export enum EResidenceStatusDB {
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT = 'MOVED_OUT',
  NOTICE = 'NOTICE',
}
export enum EResidenceStatusUi {
  MOVED_IN = 'Moved in',
  MOVED_OUT = 'Moved out',
  NOTICE = 'Noticed',
}

export enum EResidenceStatusesDB {
  APPLICANT = 'APPLICANT',
  REJECTED = 'REJECTED',
  FUTURE_RESIDENT = 'FUTURE_RESIDENT',
  RETRACTED = 'RETRACTED',
  MOVED_OUT = 'MOVED_OUT',
  RESIDENT = 'RESIDENT',
  FORMER_RESIDENT = 'FORMER_RESIDENT',
  NOTICE = 'NOTICE',
  DENIED = 'DENIED',
}

export enum EResidenceStatusesUI {
  APPLICANT = 'Applicant',
  REJECTED = 'Not Approved',
  FUTURE_RESIDENT = 'Future Resident',
  RETRACTED = 'Retracted',
  MOVED_OUT = 'Moved Out',
  RESIDENT = 'Resident',
  FORMER_RESIDENT = 'Former Resident',
  NOTICE = 'Noticed',
  DENIED = 'Not Approved',
}

export enum EResidencyFilterStatusDB {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  MOVED_IN = 'MOVED_IN',
}
export enum EResidencyFilterStatusUI {
  PENDING = 'Applicant',
  APPROVED = 'Approved',
  MOVED_IN = 'Moved In',
}

export type TResidentSearchUI = TMultiOptionTypeMaterialUI;
export type TResidentSearchDB = { residentId: string; name: string };

export enum EPooCompaniesUi {
  POO_PRINTS = 'Poo Prints',
  MR_DOG_POOP = 'Mr. Dog Poop',
  OTHER = 'other',
}

export enum EPooCompaniesDB {
  POO_PRINTS = 'POO_PRINTS',
  MR_DOG_POOP = 'MR_DOG_POOP',
  OTHER = 'OTHER',
}

export type TCompanyDB = {
  companyOption: EPooCompaniesDB | string | null;
  companyCustomName: string | null;
};

export type TCompanyUi = {
  label: EPooCompaniesUi | string | null;
  value: EPooCompaniesDB | string | null;
};

export type TCollectDNAUi = {
  residenceId: string;
  animalResidencyId: string;
  company?: TOptionTypeUi;
  dnaNumber?: string;
};

export type TCollectDNA = {
  residenceId: string;
  animalResidencyId: string;
  company?: TCompanyDB;
  dnaNumber?: string;
};

export type TUnCollectDNAUi = {
  residenceId: string;
  animalResidencyId: string;
};

export type TUnCollectDNA = {
  residenceId: string;
  animalResidencyId: string;
};

export enum EAnimalResidencyStatusUI {
  PENDING = 'Pending',
  NOT_APPROVED = 'Not Approved',
  ARCHIVED = 'Archived',
  APPROVED = 'Future Animal',
  MOVED_OUT = 'Moved Out',
  RETRACTED = 'Retracted',
  MOVED_IN = 'Moved In',
  CONDITIONALLY_MOVED_IN = 'Conditionally Moved In',
  CONDITIONALLY_APPROVED = 'Conditionally Approved',
  ABANDONED = 'Abandoned',
}

export enum EAnimalResidencyStatusDB {
  PENDING = 'PENDING',
  NOT_APPROVED = 'NOT_APPROVED',
  ARCHIVED = 'ARCHIVED',
  APPROVED = 'APPROVED',
  MOVED_OUT = 'MOVED_OUT',
  RETRACTED = 'RETRACTED',
  MOVED_IN = 'MOVED_IN',
  CONDITIONALLY_MOVED_IN = 'CONDITIONALLY_MOVED_IN',
  CONDITIONALLY_APPROVED = 'CONDITIONALLY_APPROVED',
  ABANDONED = 'ABANDONED',
}

// The application half of the split animal status, exactly the backend's vocabulary. Kept
// apart from the legacy combined-status enums, which stay untouched for existing surfaces.
export enum EAnimalApplicationStatusDB {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  NOT_APPROVED = 'NOT_APPROVED',
  ARCHIVED = 'ARCHIVED',
  RETRACTED = 'RETRACTED',
  CONDITIONALLY_APPROVED = 'CONDITIONALLY_APPROVED',
  ABANDONED = 'ABANDONED',
  APPROVED_UNTIL_RENEWAL = 'APPROVED_UNTIL_RENEWAL',
}

export enum EAnimalApplicationStatusUI {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  NOT_APPROVED = 'Not Approved',
  ARCHIVED = 'Archived',
  RETRACTED = 'Retracted',
  CONDITIONALLY_APPROVED = 'Conditionally Approved',
  ABANDONED = 'Abandoned',
  APPROVED_UNTIL_RENEWAL = 'Approved until Renewal',
}

export enum EAnimalResidencyTagUI {
  MOVED_IN = 'Moved In',
  MOVED_OUT = 'Moved Out',
}

export enum EAnimalResidencyTagDB {
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT = 'MOVED_OUT',
  NONE = 'NONE',
}

// Table filter values for the residency tag. NONE selects animals with no tag.
export enum EAnimalResidencyTagFilterDB {
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT = 'MOVED_OUT',
  NONE = 'NONE',
}

export enum EResidencyStatusUI {
  RESIDENT = 'Resident',
  APPLICANT = 'Applicant',
  FUTURE_RESIDENT = 'Future Resident',
  FORMER_RESIDENT = 'Former Resident',
  DENIED = 'Not Approved',
  RETRACTED = 'Retracted',
  NOTICE = 'Noticed',
}

export enum EResidencyStatusDB {
  RESIDENT = 'RESIDENT',
  APPLICANT = 'APPLICANT',
  FUTURE_RESIDENT = 'FUTURE_RESIDENT',
  FORMER_RESIDENT = 'FORMER_RESIDENT',
  DENIED = 'DENIED',
  RETRACTED = 'RETRACTED',
  NOTICE = 'NOTICE',
}
export enum EResidentHasAnimalDB {
  HAS_NOT_ANIMAL = 'HAS_NOT_ANIMAL',
  HAS_ANIMAL = 'HAS_ANIMAL',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

export enum EResidentHasAnimalUI {
  HAS_NOT_ANIMAL = 'HAS_NOT_ANIMAL',
  HAS_ANIMAL = 'HAS_ANIMAL',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

export enum EAnimalIndicationDB {
  UNSPECIFIED = 'UNSPECIFIED',
  HAS_ANIMAL = 'HAS_ANIMAL',
  NO_ANIMAL = 'NO_ANIMAL',
}

export enum EAnimalIndicationUI {
  UNSPECIFIED = 'Unspecified',
  HAS_ANIMAL = 'Has Animal',
  NO_ANIMAL = 'No Animal',
}

export type TAnimalReapplyDetailsUI = {
  applicationId: string;
  reason?: string;
};

export type TAnimalMoveOutRevokeDetailsUI = {
  animalResidencyId: string;
  reason: string;
};

export type TAnimalIndicationUpdateUI = {
  tenantResidencyId: string;
  hasAnimal: boolean;
  roommateHasAnimal: boolean | null;
  animalCount: number | null;
  currentRoommateAnimalIndication: EAnimalIndicationUI;
  animalReapplyDetails?: TAnimalReapplyDetailsUI[];
  animalMoveOutRevokeDetails?: TAnimalMoveOutRevokeDetailsUI[];
};

export type TAnimalIndicationUpdateDB = {
  tenantResidencyId: string;
  animalIndication: EAnimalIndicationDB;
  roommateAnimalIndication: EAnimalIndicationDB;
  animalCount: number | null;
  animalReapplyDetails: TAnimalReapplyDetailsUI[];
  animalMoveOutRevokeDetails: TAnimalMoveOutRevokeDetailsUI[];
};

export enum EAnimalIndicationActionTypeDB {
  RESIDENT_ANIMAL_INDICATION_UPDATED = 'RESIDENT_ANIMAL_INDICATION_UPDATED',
  ROOMMATE_ANIMAL_INDICATION_UPDATED = 'ROOMMATE_ANIMAL_INDICATION_UPDATED',
  RESIDENT_ANIMAL_COUNT_UPDATED = 'RESIDENT_ANIMAL_COUNT_UPDATED',
  ROOMMATE_ANIMAL_COUNT_UPDATED = 'ROOMMATE_ANIMAL_COUNT_UPDATED',
}

export type TAnimalIndicationChangeDB = {
  actionType: EAnimalIndicationActionTypeDB;
  oldValue: string | null;
  newValue: string | null;
  indicatedAnimalCount: number | null;
  occurredOn: string;
  changedBy: string | null;
};

export type TAnimalIndicationHistoryDB = {
  changes: TAnimalIndicationChangeDB[];
};

export type TAnimalIndicationChangeUI = {
  label: string;
  oldDisplay: string;
  newDisplay: string;
  occurredOn: string;
  changedBy: string;
};

export type TResidentProfileUI = {
  name: string;
  profilePicture: string;
  email: string;
  phoneNumber: string;
};

export type TResidentProfileDB = {
  name: string;
  profilePicture: string | null;
  email: string | null;
  phoneNumber: string | null;
};

export type TResidentResidencyDB = {
  residenceId: string;
  propertyId: string;
  propertyName: string;
  unitName: string;
  moveInDate: string;
  leaseToDate: string;
  nextLeaseToDate: string | null;
  unitId: string | null;
};

export type TApplicantResidencyDB = {
  residencyApplicationId: string;
  propertyId: string;
  propertyName: string;
  unitName: string;
  moveInDate: string | null;
  leaseToDate: string | null;
  nextLeaseToDate: string | null;
};
export type TResidentResidencyUI = {
  id: string;
  propertyId: string;
  propertyName: string;
  unitName: string;
  moveInDate: string | null;
  leaseToDate: string | null;
  nextLeaseToDate: string | null;
  unitId: string | null;
};

export type TResidentAnimalDB = {
  type: EAnimalTypes;
  name: string;
  profilePicture: string | null;
  species: string | null;
  speciesPredefinedId?: string;
  breed: string | null;
  weight: TAnimalWeightDB | null;
  verificationStatus: EVerificationStatus;
  residencyStatus: EAnimalResidencyStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  id: string;
  existsNonApprovedAnimalApplication: string | null;
  canNotReapplyReason: ECanNotReapplyReason | null;
  statusChangeInfoDto: TStatusChangeInfoDB;
  isSigned: boolean;
  signedBy: string | null;
  dateSigned: string | null;
  policyName: string | null;
  policyContent: string | null;
  policyId: string | null;
  paidUntil: string | null;
  paymentStatus: EAnimalPaymentStatusDB | null;
  animalId: string;
};

export type TResidentAnimalUI = {
  type: EAnimalTypesUI;
  name: string;
  profilePicture: string;
  species: string;
  speciesPredefinedId?: string;
  breed: string;
  weight: string;
  verificationStatus: EVerificationStatusUi;
  residencyStatus: EAnimalResidencyStatusUI;
  // The same nested shape the animal detail page uses, beside the legacy residencyStatus above.
  splitStatus: TAnimalStatusDetailsUI;
  id: string;
  existsNonApprovedAnimalApplication: string | null;
  canNotReapplyReason: ECanNotReapplyReason | null;
  statusChangeInfo: TStatusChangeInfoUI;
  isSigned: boolean;
  signedBy: string;
  dateSigned: string;
  policyName: string | null;
  policyContent: Blob | null;
  policyId: string | null;
  paidUntil: string;
  paymentStatus: EAnimalPaymentStatusUI | null;
  animalId: string;
};

export enum EResidentTagsDB {
  KNOWN = 'KNOWN',
}
export enum EResidentTagsUI {
  KNOWN = 'Known',
}

export type TResidentDetailsDB = {
  isPropertyCustom: boolean;
  isAutomaticMoveOutEnabled: boolean;
  residencyStatus: EResidencyStatusDB;
  residentId: string;
  residenceId: string;
  propertyId: string;
  propertyName: string;
  unitName: string;
  moveInDate: string;
  leaseToDate: string;
  name: string;
  profilePicture: string | null;
  email: string | null;
  phoneNumber: string | null;
  policyId: string | null;
  policyName: string | null;
  policyState: string | null;
  petStatus: EResidentHasAnimalDB;
  roommatePetStatus: EResidentHasAnimalDB;
  animalIndication: EAnimalIndicationDB;
  roommateAnimalIndication: EAnimalIndicationDB;
  animals: TResidentAnimalDB[];
  residencyStatusChangeInfo: TStatusInfoWithSourceUI | null;
  tags: EResidentTagsDB[];
  statusUpdateLocked: boolean;
  isScreeningEnabled: boolean;
  nextLeaseToDate: string | null;
  unitId: string | null;
  animalCount: number | null;
  roommateAnimalCount: number | null;
  isEmployee: boolean;
  shouldTenantSignAddendum: boolean;
  priceForFirstAnimal: number | null;
  pricesForOtherAnimals: number | null;
  paymentAgreementDate: string | null;
  petPaymentEnabledForPropertyManagement: boolean;
  contactId: string | null;
  deactivationStatusChangeInfo?: TTenantDeactivationStatusDB | null;
};

export type TResidentDetailsUI = {
  isAutomaticMoveOutEnabled: boolean;
  propertyCustom: boolean;
  residencyStatus: EResidencyStatusUI;
  residentId: string;
  residentProfile: TResidentProfileUI;
  residentResidency: TResidentResidencyUI;
  policy: TPolicyUI;
  petStatus: EResidentHasAnimalUI;
  roommatePetStatus: EResidentHasAnimalUI;
  animalIndication: EAnimalIndicationUI;
  roommateAnimalIndication: EAnimalIndicationUI;
  animals: TResidentAnimalUI[];
  residencyStatusChangeInfo: TStatusInfoWithSourceUI | null;
  tags: EResidentTagsUI[];
  statusUpdateLocked: boolean;
  isScreeningEnabled: boolean;
  animalCount: number | null;
  roommateAnimalCount: number | null;
  activeAnimalsCount: number;
  isEmployee: boolean;
  shouldTenantSignAddendum: boolean;
  priceForFirstAnimal: number | null;
  pricesForOtherAnimals: number | null;
  paymentAgreementDate: string;
  petPaymentEnabledForPropertyManagement: boolean;
  contactId: string | null;
  deactivationStatusChangeInfo?: TTenantDeactivationStatusUI | null;
};

export type TApplicantDetailsDB = {
  residencyStatus: EResidencyStatusDB;
  residentId: string;
  residencyApplicationId: string;
  propertyId: string;
  propertyName: string;
  unitName: string;
  moveInDate: string;
  leaseToDate: string;
  nextLeaseToDate: string | null;
  name: string;
  profilePicture: string | null;
  email: string | null;
  phoneNumber: string | null;
  policyId: string;
  policyName: string;
  policyState: string;
  petStatus: EResidentHasAnimalDB;
  roommatePetStatus: EResidentHasAnimalDB;
  animals: TResidentAnimalDB[];
  residencyStatusChangeInfo: TStatusInfoWithSourceDB | null;
  isScreeningEnabled: boolean;
};

export type TApplicantDetailsUI = {
  residencyStatus: EResidencyStatusUI;
  residentId: string;
  residentProfile: TResidentProfileUI;
  residentResidency: TResidentResidencyUI;
  policy: TPolicyUI;
  petStatus: EResidentHasAnimalUI;
  roommatePetStatus: EResidentHasAnimalUI;
  animals: TResidentAnimalUI[];
  residencyStatusChangeInfo: TStatusInfoWithSourceUI | null;
  isScreeningEnabled: boolean;
};

export type TResidencyApplicationApplyDB = {
  tenantResidencyId: string;
  propertyName: string;
  unitNumber?: string | null;
  moveInDate: string;
  leaseToDate: string | null;
};

export type TResidencyApplicationApplyUI = {
  id: string;
  /**
   * `name` is the property's actual name and is what gets submitted; `label` is what the dropdown shows and
   * also carries the address, so the two are no longer interchangeable. The approve command resolves the
   * property by exact name, so submitting the label would fail to match (PP-10045).
   */
  propertyName: TOptionTypeUi & { name: string };
  unitNumber?: TOptionTypeUi;
  moveInDate: Moment;
  leaseToDate?: Moment;
};

export type TResidencyApplicationRejectDB = {
  residencyId: string;
  reason: string;
};

export type TResidencyApplicationRejectUI = {
  id: string;
  reason: string;
};

export type TResidentDuplicateDB = {
  id: string;
  name: string;
  username: string;
};
export type TResidentDuplicateUI = {
  id: string;
  name: string;
  username: string;
};
export type TCheckTenantDuplicationParamUI = {
  unitNumber?: string;
  propertyId?: string;
  residentName?: string;
};

export type TResidentAnimalsDB = {
  applicationId: string;
  name: string;
  profilePicture: string | null;
  status: EAnimalResidencyStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  type: EAnimalTypes;
  verificationStatus: EVerificationStatus;
  specie: TSpeciesProps | null;
  breed: string | null;
};

export type TResidentAnimalsUI = {
  id: string;
  name: string;
  profilePicture: string | null;
  splitStatus: TAnimalStatusDetailsUI;
  type: EAnimalTypesUI;
  verificationStatus: EVerificationStatusUi;
  specie?: string;
  speciePredefinedId?: string;
  breed: string | null;
};

export type EResidencyMoveInDB = {
  tenantResidencyId: string;
  propertyId: string;
  unitName: string;
  leaseToDate: string;
  moveInDate: string;
  applicationIds: string[];
};

export type EResidencyMoveInUI = {
  id: string;
  property: { value: TPropertyUI | string; label: string };
  unit: {
    __isNew__: boolean;
    value: TUnitUI | string;
    label: string;
  };
  leaseToDate: Moment;
  moveInDate: Moment;
  applicationIds: string[];
};

export type TRetractAnimalDB = {
  id: string;
  name: string;
  profilePicture: string | null;
  status: EAnimalResidencyStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  type: EAnimalTypes;
  verificationStatus: EVerificationStatus;
  specie: TSpeciesProps | null;
  breed: string | null;
};
export type TRetractAnimalUI = {
  id: string;
  name: string;
  profilePicture: string | null;
  splitStatus: TAnimalStatusDetailsUI;
  type: EAnimalTypesUI;
  verificationStatus: EVerificationStatusUi;
  specie?: string;
  speciePredefinedId?: string;
  breed: string | null;
};

export type TRevokeAnimalDB = {
  animalResidencyId: string;
  id: string;
  name: string;
  profilePicture: string | null;
  status: EAnimalResidencyStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  type: EAnimalTypes;
  verificationStatus: EVerificationStatus;
  specie: TSpeciesProps | null;
  breed: string | null;
};
export type TRevokeAnimalUI = {
  animalId: string;
  id: string;
  name: string;
  profilePicture: string | null;
  splitStatus: TAnimalStatusDetailsUI;
  type: EAnimalTypesUI;
  verificationStatus: EVerificationStatusUi;
  specie?: string;
  speciePredefinedId?: string;
  breed: string | null;
};
export enum EUnitAnimalPresenceStatusDB {
  YES = 'YES',
  NO = 'NO',
  UNSPECIFIED = 'UNSPECIFIED',
  CONFLICT = 'CONFLICT',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}
export enum EUnitAnimalPresenceStatusUI {
  YES = 'Yes',
  NO = 'No',
  UNSPECIFIED = 'Unspecified',
  CONFLICT = 'Conflict',
  NOT_APPLICABLE = 'Not applicable',
}

export type TTenantDeactivationStatusUI = Pick<TStatusChangeInfoUI, 'by' | 'date' | 'reason'>;
export type TTenantDeactivationStatusDB = Pick<TStatusChangeInfoDB, 'by' | 'date' | 'reason'>;

export type TResidentsTableDB = {
  isPropertyCustom: boolean;
  actionActorName: string;
  actionReason: string | null;
  actionTime: string;
  canApprove: boolean;
  canMoveIn: boolean;
  canMoveOut: boolean;
  canReject: boolean;
  canRevokeApproval: boolean;
  canRevokeMoveIn: boolean;
  canRevokeNotice: boolean;
  canNotice: boolean;
  email: string | null;
  id: string;
  leaseToDate: string | null;
  moveInDate: string | null;
  name: string;
  numOfAnimals: number;
  petStatus: EPetStatusDB;
  phone: string | null;
  policyId: string | null;
  policyName: string | null;
  propertyId: string | null;
  propertyName: string | null;
  residencyStatus: EResidencyStatusDB;
  statusChangeInfo: TStatusInfoWithSourceDB;
  tenantId: string;
  unitId: string | null;
  unitNumber: string | null;
  numOfUnitAnimals: number;
  unitAnimalPresenceStatus: EUnitAnimalPresenceStatusDB;
  isEmployee: boolean;
  deactivationStatusChangeInfo: TTenantDeactivationStatusDB | null;
  isScreeningEnabled: boolean;
};
export type TResidentsTableUI = {
  customProperty: boolean;
  actionActorName: string;
  actionReason: string | null;
  actionTime: string;
  canApprove: boolean;
  canMoveIn: boolean;
  canMoveOut: boolean;
  canReject: boolean;
  canRevokeApproval: boolean;
  canRevokeMoveIn: boolean;
  canRevokeNotice: boolean;
  canNotice: boolean;
  email: string | null;
  id: string;
  leaseToDate: string | null;
  moveInDate: string | null;
  name: string;
  numOfAnimals: number;
  petStatus: EPetStatusUI;
  phone: string | null;
  policyId: string | null;
  policyName: string | null;
  propertyId: string | null;
  propertyName: string | null;
  residencyStatus: EResidencyStatusUI;
  statusChangeInfo: TStatusInfoWithSourceUI | null;
  tenantId: string;
  unitId: string | null;
  unitNumber: string | null;
  unitAnimalPresenceStatus: EUnitAnimalPresenceStatusUI;
  numOfUnitAnimals: number;
  isEmployee: boolean;
  deactivationStatusChangeInfo?: TTenantDeactivationStatusUI | null;
  isScreeningEnabled: boolean;
};

export type TAddNewResidentDB = {
  emailAddress: string;
  leaseToDate: string;
  moveInDate: string;
  name: string;
  phoneNumber?: string;
  propertyId: string;
  status: string;
  unitId?: string;
  // PP-10249: set instead of unitId when the user typed a unit that doesn't exist in OPP yet.
  unitNumber?: string;
  needToScreen: boolean;
};
export type TAddNewResidentUI = {
  emailAddress: string;
  startTime: Moment;
  endTime: Moment;
  name: string;
  phoneNumber?: string;
  property: TOptionTypeUi & { rentalId: string };
  status: string;
  unit?: TOptionTypeUi;
  needToScreen: boolean;
};

export type TAnimalsScreeningUI = {
  tenantResidencyId: string;
  needToScreen: boolean;
  // Only sent when turning screening off — locks the settings so automation can't re-enable it.
  lock?: boolean;
};

export type TAnimalsScreeningDB = {
  tenantResidencyId: string;
  needToScreen: boolean;
  lock?: boolean;
};

export enum EPetStatusDB {
  HAS_ANIMAL = 'HAS_ANIMAL',
  HAS_NOT_ANIMAL = 'HAS_NOT_ANIMAL',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

export enum EPetStatusUI {
  HAS_ANIMAL = 'Yes',
  HAS_NOT_ANIMAL = 'No',
  NOT_SPECIFIED = 'Not specified',
}

export type TBulkScreeningDB = {
  needToScreen: boolean;
  lock?: boolean;
};
export type TBulkScreeningUI = {
  needToScreen: boolean;
  lock?: boolean;
  search: string;
};

export enum EAnimalRestrictionStatusDB {
  RESTRICTED = 'RESTRICTED',
  NOT_RESTRICTED = 'NOT_RESTRICTED',
  NEED_MORE_INFORMATION = 'NEED_MORE_INFORMATION',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}
export enum EAnimalRestrictionStatusUI {
  RESTRICTED = 'Yes',
  NOT_RESTRICTED = 'No',
  NEED_MORE_INFORMATION = 'Need More Information',
  NOT_APPLICABLE = 'N/A',
  NOT_SPECIFIED = 'Not Specified',
}

export enum EAnimalRestrictionTagDB {
  SPECIES = 'SPECIES',
  BREED = 'BREED',
  WEIGHT = 'WEIGHT',
  AGE = 'AGE',
  EXCEEDS_MAX_PET_NUMBER = 'EXCEEDS_MAX_PET_NUMBER',
  NO_PETS_ALLOWED = 'NO_PETS_ALLOWED',
  OTHER = 'OTHER',
}
export enum EAnimalRestrictionTagUI {
  SPECIES = 'Species',
  BREED = 'Breed',
  WEIGHT = 'Weight',
  AGE = 'Age',
  EXCEEDS_MAX_PET_NUMBER = 'Exceeds Max # of Pets',
  NO_PETS_ALLOWED = 'No Pets Allowed',
  OTHER = 'Other',
}
